import { RoleWithDetails } from "@boark/shared/types/role-with-details";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetAuthContext } from "../../queries/get-auth-context";
import { useGetRoles } from "../../queries/role";
import { formatAssignedUsers } from "../../services/role";
import { useLocale } from "../../stores/locale";
import { recordIdToId } from "../../utils/record-id-to-id";
import { stripHtml } from "../../utils/strip-html";
import { RoleCreate } from "./role-create";
import { RoleDelete } from "./role-delete";
import { RoleEdit } from "./role-edit";

export function Roles() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: roles, isLoading: isRolesLoading } = useGetRoles();
  const { data: authContext } = useGetAuthContext();
  const isOwner =
    authContext?.type === "associationUser" && authContext.isOwner;

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Routes>
        <Route element={<RoleCreate />} path="/create" />
        <Route element={<RoleEdit />} path="/:id/edit" />
        <Route element={<RoleDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.role.roles}</H1>
          {isOwner && (
            <Button route="/admin/role/create">
              {t.role.create}
            </Button>
          )}
        </div>
        <Table
          data={roles || []}
          loading={isRolesLoading}
          editRouteButton={
            isOwner
              ? (row) => `/admin/role/${recordIdToId(row.id)}/edit`
              : undefined
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={
            isOwner
              ? (row) => `/admin/role/${recordIdToId(row.id)}/delete`
              : undefined
          }
          sortBy={searchParams.get("sortBy") || "name"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({ filterBy: JSON.stringify(value) });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="name">{t.role.name}</Column>
          <Column
            body={(row: RoleWithDetails) =>
              formatAssignedUsers(row) || t.role.assignedToEmpty
            }
            field="assignedTo"
          >
            {t.role.assignedTo}
          </Column>
          <Column
            body={(row: RoleWithDetails) => row.updatedBy.email}
            field="updatedBy.email"
          >
            {t.role.updatedBy}
          </Column>
        </Table>
      </div>
    </>
  );
}
